'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { useCounter } from '@/hooks/use-counter';
import { cn } from '@/lib/utils';
import { useMemo } from 'react';

interface StatItem {
  target: number;
  suffix: string;
  label: string;
}

interface StatCardProps {
  stat: StatItem;
  index: number;
  isVisible: boolean;
}

interface FeaturedStatProps {
  value: string;
  label: string;
  description: string;
  isVisible: boolean;
}

function FeaturedStatCard({ value, label, description, isVisible }: FeaturedStatProps) {
  // Extract number and suffix from value (e.g., "25" from "25 Years")
  const match = value.match(/^([\d,]+)(.*)$/);
  const numericValue = match ? parseInt(match[1].replace(/,/g, ''), 10) : 0;
  const suffix = match ? match[2].trim() : '';

  const count = useCounter({
    target: numericValue,
    duration: 2000,
    startOnVisible: true,
    isVisible,
    delay: 0,
  });

  const formattedCount = numericValue > 0 ? count.toLocaleString('en-US') : '0';

  return (
    <div
      className={cn(
        "relative rounded-2xl p-8 md:p-12 lg:p-16 h-full min-h-[400px] flex flex-col justify-between",
        "bg-gradient-to-br from-primary-600/90 via-primary-700/80 to-gray-900 dark:from-primary-700/90 dark:via-primary-800/80 dark:to-black",
        "transition-all duration-700",
        isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
      )}
    >
      <div>
        <div className="mb-6">
          <Typography
            variant="h2"
            className="text-6xl md:text-7xl lg:text-8xl font-bold text-primary-300 dark:text-primary-200 leading-none"
          >
            {numericValue > 0 ? `${formattedCount}${suffix}` : value}
          </Typography>
          <div className="flex flex-col justify-start pt-2">
            <Typography
              variant="h3"
              className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight"
            >
              {label}
            </Typography>
          </div>
        </div>
        <Typography variant="p" className="text-white/90 text-base md:text-lg leading-relaxed max-w-md">
          {description}
        </Typography>
      </div>
    </div>
  );
}

function StatCard({ stat, index, isVisible }: StatCardProps) {
  const count = useCounter({
    target: stat.target,
    duration: 2000,
    startOnVisible: true,
    isVisible,
    delay: index * 100,
  });

  // Format number with commas for thousands
  const formattedCount = stat.target > 0
    ? count.toLocaleString('en-US')
    : '0';

  return (
    <div
      className={cn(
        "bg-gray-700 dark:bg-black rounded-xl p-6 md:p-8 flex flex-col justify-center items-center text-center",
        "transition-all duration-700",
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      )}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <Typography
        variant="h2"
        className="mb-3 text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-none"
      >
        {stat.target > 0 ? `${formattedCount}${stat.suffix}` : stat.suffix}
      </Typography>
      <Typography variant="p" className="text-white/80 text-sm md:text-base">
        {stat.label}
      </Typography>
    </div>
  );
}

export function StatsSection() {
  const t = useTranslations('aboutPage.stats');
  const sectionRef = useScrollAnimation({ threshold: 0.15 });

  // Get featured stat
  const featuredStat = useMemo(() => {
    const value = t('featured.value');
    const label = t('featured.label');
    const description = t('featured.description');
    return { value, label, description };
  }, [t]);

  // Parse stats values and extract numeric targets
  const stats = useMemo<StatItem[]>(() => {
    const statKeys = ['stat1', 'stat2', 'stat3', 'stat4'];
    return statKeys.map((key) => {
      const value = t(`${key}.value`);
      const label = t(`${key}.label`);

      // Extract number and suffix (e.g., "1000+" -> { target: 1000, suffix: "+" })
      // Match digits (with optional commas) followed by any suffix
      const match = value.match(/^([\d,]+)(.*)$/);
      if (match) {
        const numericValue = parseInt(match[1].replace(/,/g, ''), 10);
        const suffix = match[2] || '';
        const result = {
          target: isNaN(numericValue) ? 0 : numericValue,
          suffix,
          label,
        };
        return result;
      }

      return {
        target: 0,
        suffix: value,
        label,
      };
    });
  }, [t]);

  return (
    <SectionWrapper
      ref={sectionRef.ref}
      variant="dark"
      padding="lg"
      className="relative overflow-hidden bg-gray-900 dark:bg-gray-950 border-t-0"
    >
      <div className="container relative z-10 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Featured Stat Card - Left Side */}
          <div className="lg:col-span-1">
            <FeaturedStatCard
              value={featuredStat.value}
              label={featuredStat.label}
              description={featuredStat.description}
              isVisible={sectionRef.isVisible}
            />
          </div>

          {/* Stats Grid - Right Side (2x2) */}
          <div className="lg:col-span-2 grid grid-cols-2 gap-6 md:gap-8">
            {stats.map((stat, index) => (
              <StatCard
                key={`${stat.target}-${stat.label}-${index}`}
                stat={stat}
                index={index}
                isVisible={sectionRef.isVisible}
              />
            ))}
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
}

